import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Globe,
  Phone,
  MapPin,
  Link,
  Briefcase,
  FileText,
  Users,
  Upload,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Company, UpdateCompanyData, companyAPI } from "@/services/companyService";
import { industryAPI } from "@/services/industryService";
import { CreateIndustryModal } from "@/components/companies/CreateIndustryModal";
import { IndustryOption, CreateIndustryData } from "@/types/industry";
import { FileUploader, Attachment } from "@/components/shared/FileUploader";
import { contactoAPI } from "@/services/contactoService";
import { CountryCodeSelect } from "@/components/shared/CountryCodeSelect";
import { countries } from "@/data/countries";

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, companyData: UpdateCompanyData, contactData?: any, fileToUpload?: File) => Promise<void>;
  company: Company | null;
  companySlug: string;
}

const SIZE_OPTIONS = [
  { value: "1-10", label: "1-10 empleados (Pequeña)" },
  { value: "11-50", label: "11-50 empleados (Pequeña)" },
  { value: "51-100", label: "51-100 empleados (Mediana)" },
  { value: "101-250", label: "101-250 empleados (Mediana)" },
  { value: "251-500", label: "251-500 empleados (Grande)" },
  { value: "501-1000", label: "501-1000 empleados (Grande)" },
  { value: "1001-5000", label: "1001-5000 empleados (Grande)" },
  { value: "5001-10000", label: "Más de 5000 empleados (Grande)" },
];

export function EditCompanyModal({
  isOpen,
  onClose,
  onSave,
  company,
  companySlug,
}: EditCompanyModalProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("company");
  const [formData, setFormData] = useState<UpdateCompanyData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [countryCode, setCountryCode] = useState("+503");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [contactData, setContactData] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    countryCode: "+503",
  });
  const [contactLoading, setContactLoading] = useState(false);

  const loadIndustries = async () => {
    try {
      setIndustriesLoading(true);
      const industryOptions = await industryAPI.getIndustryOptions(companySlug);
      setIndustries(industryOptions);
      return industryOptions;
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las industrias",
        variant: "destructive",
      });
      return [];
    } finally {
      setIndustriesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && company) {
      setContactLoading(true);
      contactoAPI.getContactos(companySlug, { client_company_id: company.id })
        .then((response: any) => {
           const contacts = Array.isArray(response) ? response : (response.data || []);
           if (contacts.length > 0) {
             const oldestContact = contacts.sort((a: any, b: any) => 
               new Date(a.createdAt || a.created_at).getTime() - new Date(b.createdAt || b.created_at).getTime()
             )[0];
             let cleanPhone = oldestContact.phone || "";
             let code = oldestContact.countryCode || "";

             if (code && cleanPhone.startsWith(code)) {
               cleanPhone = cleanPhone.substring(code.length).trim();
             } else if (!code && cleanPhone.startsWith("+")) {
               const matchedCountry = countries.find((c) =>
                 cleanPhone.startsWith(c.code),
               );
               if (matchedCountry) {
                 code = matchedCountry.code;
                 cleanPhone = cleanPhone.substring(code.length).trim();
               }
             }

             setContactData({
                id: oldestContact.id || "",
                firstName: oldestContact.firstName || oldestContact.name?.split(' ')[0] || "",
                lastName: oldestContact.lastName || oldestContact.name?.split(' ').slice(1).join(' ') || "",
                email: oldestContact.email || "",
                phone: cleanPhone,
                position: oldestContact.position || "",
                countryCode: code || "+503",
             });
           } else {
             setContactData({
                id: "",
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                position: "",
                countryCode: "+503",
             });
           }
        })
        .catch(err => console.error("Error fetching contacts:", err))
        .finally(() => setContactLoading(false));

      loadIndustries().then((options) => {
        let industryId = company.industry_id ? String(company.industry_id) : "";

        const exists = options.some((opt) => opt.value === industryId);

        if ((!industryId || !exists) && company.industry) {
          const ind = company.industry as any;
          let foundOption;

          if (typeof ind === "object") {
            if (ind.id) {
              const objId = String(ind.id);
              foundOption = options.find((opt) => opt.value === objId);
            }
            if (!foundOption && ind.name) {
              const searchName = String(ind.name).trim().toLowerCase();
              foundOption = options.find(
                (opt) => opt.label.trim().toLowerCase() === searchName,
              );
            }
          } else if (typeof ind === "string") {
            const searchName = ind.trim().toLowerCase();
            foundOption = options.find(
              (opt) => opt.label.trim().toLowerCase() === searchName,
            );
          }

          if (foundOption) {
            industryId = foundOption.value;
          }
        }

        let sizeValue = company.size || "";
        // Normalizar cualquier formato anterior (con ceros o espacios) al formato simple
        if (sizeValue === "001-010" || sizeValue === "1 - 10")
          sizeValue = "1-10";
        if (sizeValue === "011-050" || sizeValue === "11 - 50")
          sizeValue = "11-50";
        if (sizeValue === "51 - 100") sizeValue = "51-100";

        let phoneWithoutCode = company.phone || "";
        let foundCode = "+503";
        
        if (phoneWithoutCode.startsWith("+")) {
           const matchedCountry = countries.find((c) =>
             phoneWithoutCode.startsWith(c.code)
           );
           if (matchedCountry) {
             foundCode = matchedCountry.code;
             phoneWithoutCode = phoneWithoutCode.substring(foundCode.length).trim();
           }
        }
        setCountryCode(foundCode);

        setFormData({
          name: company.name,
          industry_id: company.industry_id || "",
          size: sizeValue,
          phone: phoneWithoutCode,
          website: company.website || "",
          address: company.address || "",
          rtu_url: company.rtu_url || "",
          notes: company.notes || "",
          slug: companySlug,
        });
        
        if (company.rtu_url) {
          const fileName = company.rtu_url.split('/').pop()?.split('?')[0] || "Documento Adjunto";
          const mockFile = new File([""], decodeURIComponent(fileName), { type: "application/pdf" });
          setAttachments([{ file: mockFile, description: "Archivo actual" }]);
        } else {
          setAttachments([]);
        }
      });

      setErrors({});
    }
  }, [isOpen, company, companySlug]);

  const handleChange = (field: keyof UpdateCompanyData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) newErrors.name = "Este campo es obligatorio";
    if (!formData.industry_id)
      newErrors.industry_id = "Selecciona una industria";

    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Formato no válido";
    }

    const urlRegex = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\- .\/?%&=]*)?$/;
    if (contactData.email) {
      if (!/\S+@\S+\.\S+/.test(contactData.email)) {
        newErrors.contactEmail = "Email de contacto no válido";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa los campos obligatorios",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTab === "company") {
      if (!formData.name?.trim() || !formData.industry_id) {
        toast({
          title: "Campos requeridos",
          description: "Por favor, completa el nombre y la industria antes de continuar.",
          variant: "destructive",
        });
        return;
      }
      setActiveTab("contact");
      return;
    }

    if (!company || !validate()) return;

    setLoading(true);
    try {
      const submitData: UpdateCompanyData = {
        ...formData,
        name: formData.name?.trim(),
        phone: formData.phone ? `${countryCode} ${formData.phone}`.trim() : "",
        notes: formData.notes || "",
      };
      
      // We pass the attachment file directly to onSave and await the whole process
      await onSave(company.id, submitData, contactData, attachments[0]?.file);
      
      setAttachments([]);
      onClose();
    } catch (error: any) {
      console.error("Error in EditCompanyModal handleSubmit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndustry = async (data: {
    name: string;
    is_default: boolean;
  }) => {
    try {
      const industryData: CreateIndustryData = {
        name: data.name,
        is_default: data.is_default,
        is_active: true,
        slug: companySlug,
      };

      const response = await industryAPI.createIndustry(industryData);

      if (response.success) {
        await loadIndustries();

        if (response.data?.id) {
          setFormData((prev) => ({
            ...prev,
            industry_id: response.data.id,
          }));
        }

        toast({
          title: "Industria creada",
          description: `La industria "${data.name}" ha sido creada exitosamente.`,
        });

        return true;
      }
      return false;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al crear la industria",
        variant: "destructive",
      });
      return false;
    }
  };



  if (!company) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
          <div className="flex flex-col flex-1 overflow-hidden relative min-h-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <DialogHeader>
                  <div className="flex items-center space-x-3">
                    <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                      <Building2 className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="text-left">
                      <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                        Editar Empresa
                      </DialogTitle>
                      <DialogDescription className="text-xs text-slate-500 mt-0.5">
                        Actualiza la información de {company.name}
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 bg-slate-100/80 p-1.5 rounded-xl h-10">
                  <TabsTrigger value="company" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Datos de Empresa</TabsTrigger>
                  <TabsTrigger value="contact" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Contacto Principal</TabsTrigger>
                </TabsList>
              </div>

              <div className="p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0">
                <div className="space-y-8">
                  <TabsContent value="company" className="space-y-8 mt-0 focus:outline-none">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Briefcase className="h-5 w-5 text-indigo-500" />
                      <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                        Información Básica
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2 space-y-2">
                        <Label
                          htmlFor="name"
                          className="text-sm font-medium text-slate-700"
                        >
                          Nombre de la Empresa{" "}
                          <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="name"
                            value={formData.name || ""}
                            onChange={(e) => handleChange("name", e.target.value)}
                            placeholder="Nombre completo de la empresa"
                            className={`h-11 pl-10 rounded-xl bg-slate-50/50 border-slate-200 focus-visible:ring-indigo-500 focus-visible:bg-white transition-colors ${
                              errors.name
                                ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50"
                                : ""
                            }`}
                          />
                          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                        {errors.name && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.name}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="industry_id"
                          className="text-sm font-medium text-slate-700"
                        >
                          Industria <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex gap-2">
                          <Select
                            key={`${company.id}-${industries.length}-${industriesLoading}`}
                            value={formData.industry_id || ""}
                            onValueChange={(value) =>
                              handleChange("industry_id", value)
                            }
                          >
                            <SelectTrigger
                              className={`flex-1 h-11 rounded-xl bg-slate-50/50 border-slate-200 focus:ring-indigo-500 transition-colors ${
                                errors.industry_id
                                  ? "border-red-500 focus:ring-red-500 bg-red-50/50"
                                  : ""
                              }`}
                            >
                              <SelectValue placeholder="Seleccionar industria" />
                            </SelectTrigger>
                            <SelectContent>
                              {industriesLoading ? (
                                <SelectItem value="loading" disabled>
                                  <div className="flex items-center justify-center py-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    <span className="ml-2">Cargando...</span>
                                  </div>
                                </SelectItem>
                              ) : industries.length === 0 ? (
                                <SelectItem value="empty" disabled>
                                  Sin industrias disponibles
                                </SelectItem>
                              ) : (
                                industries.map((industry) => (
                                  <SelectItem
                                    key={industry.value}
                                    value={industry.value}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span>{industry.label}</span>
                                      {industry.is_default && (
                                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                          Por defecto
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setIsIndustryModalOpen(true)}
                            className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                            title="Crear nueva industria"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {errors.industry_id && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.industry_id}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="size"
                          className="text-sm font-medium text-slate-700"
                        >
                          Tamaño de la Empresa
                        </Label>
                        <Select
                          value={formData.size || ""}
                          onValueChange={(value) => handleChange("size", value)}
                        >
                          <SelectTrigger
                            className={`h-11 rounded-xl bg-slate-50/50 border-slate-200 focus:ring-indigo-500 transition-colors ${
                              errors.size
                                ? "border-red-500 focus:ring-red-500 bg-red-50/50"
                                : ""
                            }`}
                            id="size"
                          >
                            <SelectValue placeholder="Seleccionar tamaño" />
                          </SelectTrigger>
                          <SelectContent>
                            {SIZE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-2 text-slate-400" />
                                  {option.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.size && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.size}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-5 w-5 text-indigo-500" />
                      <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                        Información de Contacto
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label
                          htmlFor="phone"
                          className="text-sm font-medium text-slate-700"
                        >
                          Teléfono
                        </Label>
                        <div className="flex gap-2">
                          <CountryCodeSelect
                            value={countryCode}
                            onChange={setCountryCode}
                          />
                          <div className="relative flex-1">
                            <Input
                              id="phone"
                              value={formData.phone || ""}
                              onChange={(e) => handleChange("phone", e.target.value)}
                              placeholder="Número de teléfono"
                              className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                                errors.phone
                                  ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50"
                                  : ""
                              }`}
                            />
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          </div>
                        </div>
                        {errors.phone && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.phone}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="website"
                          className="text-sm font-medium text-slate-700"
                        >
                          Sitio Web
                        </Label>
                        <div className="relative">
                          <Input
                            id="website"
                            value={formData.website || ""}
                            onChange={(e) =>
                              handleChange("website", e.target.value)
                            }
                            placeholder="URL del sitio web"
                            className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                              errors.website
                                ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50"
                                : ""
                            }`}
                          />
                          <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                        {errors.website && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.website}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="address"
                          className="text-sm font-medium text-slate-700"
                        >
                          Dirección
                        </Label>
                        <div className="relative">
                          <Input
                            id="address"
                            value={formData.address || ""}
                            onChange={(e) =>
                              handleChange("address", e.target.value)
                            }
                            placeholder="Dirección completa"
                            className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                              errors.address
                                ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50"
                                : ""
                            }`}
                          />
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                        {errors.address && (
                          <p className="text-red-500 text-sm flex items-center">
                            <span className="mr-1">•</span> {errors.address}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">
                          Documento Adjunto
                        </Label>
                        {attachments.length === 0 && formData.rtu_url ? (
                          <div className="border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="bg-emerald-100 p-2 rounded-lg">
                                <FileText className="h-5 w-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-emerald-700">Documento cargado</p>
                                <p className="text-xs text-emerald-600 truncate max-w-[240px]">
                                  {(() => {
                                    const p = formData.rtu_url.split('?')[0];
                                    try { return decodeURIComponent(p.split('/').pop() || 'Archivo'); } catch { return p.split('/').pop() || 'Archivo'; }
                                  })()}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleChange('rtu_url', '')}
                              className="text-xs text-slate-400 hover:text-red-500 transition-colors underline ml-2"
                            >
                              Reemplazar
                            </button>
                          </div>
                        ) : (
                          <FileUploader
                            attachments={attachments}
                            onAttachmentsChange={setAttachments}
                            maxFiles={1}
                            allowedTypes={["application/pdf", "image/jpeg", "image/png"]}
                            label="Haz clic o arrastra un documento aquí"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                      <div className="md:col-span-2 space-y-2">
                        <Label
                          htmlFor="notes"
                          className="text-sm font-medium text-slate-700"
                        >
                          Notas
                        </Label>
                        <Textarea
                          id="notes"
                          value={formData.notes || ""}
                          onChange={(e) => handleChange("notes", e.target.value)}
                          placeholder="Información adicional relevante..."
                          rows={4}
                          className="rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                  <TabsContent value="contact" className="space-y-8 mt-0 focus:outline-none">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-indigo-500" />
                      <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                        Contacto Principal
                      </h3>
                    </div>

                    {contactLoading ? (
                      <div className="flex justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="contactFirstName">Nombre</Label>
                          <Input
                            id="contactFirstName"
                            value={contactData.firstName}
                            onChange={(e) => setContactData(prev => ({ ...prev, firstName: e.target.value }))}
                            placeholder="Ej. Juan"
                            className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contactLastName">Apellido</Label>
                          <Input
                            id="contactLastName"
                            value={contactData.lastName}
                            onChange={(e) => setContactData(prev => ({ ...prev, lastName: e.target.value }))}
                            placeholder="Ej. Pérez"
                            className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contactEmail">Email</Label>
                          <Input
                            id="contactEmail"
                            type="email"
                            value={contactData.email}
                            onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="juan@empresa.com"
                            className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                          />
                          {errors.contactEmail && <p className="text-red-500 text-sm">{errors.contactEmail}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contactPhone">Teléfono</Label>
                          <div className="flex gap-2">
                            <CountryCodeSelect
                              value={contactData.countryCode || "+503"}
                              onChange={(val) => setContactData(prev => ({ ...prev, countryCode: val }))}
                            />
                            <Input
                              id="contactPhone"
                              value={contactData.phone}
                              onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, "") }))}
                              placeholder="Número de teléfono"
                              className="flex-1 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                            />
                          </div>
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label htmlFor="contactPosition">Cargo</Label>
                          <Input
                            id="contactPosition"
                            value={contactData.position}
                            onChange={(e) => setContactData(prev => ({ ...prev, position: e.target.value }))}
                            placeholder="Ej. Gerente General"
                            className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
                </div>
              </div>

              <DialogFooter className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10">
                <div className="flex items-center justify-between w-full">
                  <div className="text-xs text-slate-500 font-medium">
                    <span className="text-red-500">*</span> Campos obligatorios
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={loading}
                      className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                    >
                      Cancelar
                    </Button>
                    {activeTab === "company" ? (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("contact");
                        }}
                        disabled={!formData.name?.trim() || !formData.industry_id}
                        className="px-6 h-10 rounded-lg bg-slate-900 hover:bg-black text-white font-medium shadow-sm shadow-slate-900/20"
                      >
                        Siguiente
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubmit(e as any);
                        }}
                        disabled={
                          loading ||
                          !formData.name?.trim() ||
                          !formData.industry_id
                        }
                        className="px-6 h-10 rounded-lg bg-slate-900 hover:bg-black text-white font-medium shadow-sm shadow-slate-900/20"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="animate-spin h-4 w-4 mr-2" />
                            Guardando...
                          </>
                        ) : (
                          "Guardar Cambios"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogFooter>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      <CreateIndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        onSave={handleCreateIndustry}
        companySlug={companySlug}
      />
    </>
  );
}
